import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowRight, Check, Eye, EyeOff, Lock, ShieldCheck, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAuth } from '@/hooks';
import { cn } from '@/utils/cn';

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Must contain an uppercase letter')
      .regex(/[a-z]/, 'Must contain a lowercase letter')
      .regex(/\d/, 'Must contain a number')
      .regex(/[!@#$%^&*.]/, 'Must contain a special character'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

export function ChangePassword() {
  const { changePassword, isLoading, error, clearError, user } = useAuth();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  const newPassword = watch('newPassword', '');

  const passwordChecks = [
    { label: '8+ chars', valid: newPassword.length >= 8 },
    { label: 'Uppercase', valid: /[A-Z]/.test(newPassword) },
    { label: 'Lowercase', valid: /[a-z]/.test(newPassword) },
    { label: 'Number', valid: /\d/.test(newPassword) },
    { label: 'Symbol', valid: /[!@#$%^&*.]/.test(newPassword) },
  ];

  const onSubmit = async (data: ChangePasswordFormData) => {
    clearError();
    await changePassword(data);
  };

  const isPasswordReset = user?.passwordResetRequired;

  return (
    <div className="space-y-12">
      <div className="text-center space-y-4">
        <div className="w-24 h-24 mx-auto mb-8 rounded-[3rem] bg-gradient-brand flex items-center justify-center shadow-2xl shadow-green-500/20">
          <ShieldCheck className="w-12 h-12 text-white" />
        </div>
        <h2 className="text-4xl font-black tracking-tighter text-slate-900 dark:text-white leading-tight">
          {isPasswordReset ? 'Secure Your' : 'Update'}{' '}
          <span className="text-gradient-brand">Account</span>
        </h2>
        <p className="text-lg font-black text-slate-400 dark:text-white/40 uppercase tracking-widest text-xs">
          {isPasswordReset ? 'Access verification required' : 'Maintain credential integrity'}
        </p>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-6 rounded-[2rem] bg-rose-50 dark:bg-rose-500/10 border-2 border-rose-100 dark:border-rose-500/20 flex items-start gap-4 backdrop-blur-xl"
        >
          <AlertCircle className="w-6 h-6 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-rose-700 dark:text-rose-300 font-black uppercase tracking-wide">
            {error}
          </p>
        </motion.div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="space-y-2">
          <label
            htmlFor="currentPassword"
            className="text-xs font-black text-neolab-green/60 dark:text-neolab-green uppercase tracking-[0.4em] ml-6"
          >
            Current Password
          </label>
          <div className="relative group">
            <div className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-neolab-green transition-colors duration-500">
              <Lock className="w-5 h-5" />
            </div>
            <input
              id="currentPassword"
              type={showCurrentPassword ? 'text' : 'password'}
              {...register('currentPassword')}
              placeholder="••••••••"
              className={cn(
                'input-field pl-16 pr-16',
                errors.currentPassword && 'border-rose-500 focus:ring-rose-500/10',
              )}
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-6 top-1/2 -translate-y-1/2 p-2 rounded-xl text-slate-300 hover:text-neolab-green hover:bg-neolab-green/5 transition-all"
            >
              {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="newPassword"
            className="text-xs font-black text-neolab-green/60 dark:text-neolab-green uppercase tracking-[0.4em] ml-6"
          >
            New Password
          </label>
          <div className="relative group">
            <div className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-neolab-green transition-colors duration-500">
              <Lock className="w-5 h-5" />
            </div>
            <input
              id="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              {...register('newPassword')}
              placeholder="••••••••"
              className={cn(
                'input-field pl-16 pr-16',
                errors.newPassword && 'border-rose-500 focus:ring-rose-500/10',
              )}
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-6 top-1/2 -translate-y-1/2 p-2 rounded-xl text-slate-300 hover:text-neolab-green hover:bg-neolab-green/5 transition-all"
            >
              {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mt-3 ml-6">
            {passwordChecks.map((check, i) => (
              <div
                key={i}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest transition-all',
                  check.valid
                    ? 'bg-neolab-green text-white shadow-lg shadow-green-500/20'
                    : 'bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-white/20',
                )}
              >
                {check.valid ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
                {check.label}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="confirmPassword"
            className="text-xs font-black text-neolab-green/60 dark:text-neolab-green uppercase tracking-[0.2em] ml-6"
          >
            Repeat New Password
          </label>
          <div className="relative group">
            <div className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-neolab-green transition-colors duration-500">
              <Lock className="w-5 h-5" />
            </div>
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              {...register('confirmPassword')}
              placeholder="••••••••"
              className={cn(
                'input-field pl-16 pr-16',
                errors.confirmPassword && 'border-rose-500 focus:ring-rose-500/10',
              )}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-6 top-1/2 -translate-y-1/2 p-2 rounded-xl text-slate-300 hover:text-neolab-green hover:bg-neolab-green/5 transition-all"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-xs font-black text-rose-500 ml-6 mt-2 uppercase tracking-wider">
              {errors.confirmPassword?.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-6 flex items-center justify-center gap-4 group mt-6"
        >
          {isLoading ? (
            <>
              <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Updating Credentials...</span>
            </>
          ) : (
            <>
              <ShieldCheck className="w-6 h-6 transition-transform group-hover:scale-110" />
              <span>Update Credentials</span>
              <ArrowRight className="w-5 h-5 opacity-40 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>
      </form>
    </div>
  );
}

export default ChangePassword;
